/**
 * Wireline & Slickline quotes (WL-5, fieldiq-api#1359) on the shared quote
 * screens: the Wireline & Slickline Rate Card as a pricing source (`wl_rate`),
 * "Create Wireline & Slickline job" on an accepted WL quote, and every refusal
 * those doors answer with as a sentence. Well Servicing's lib/ws-quotes.ts,
 * rule for rule, on WL's card — with run kinds as the job's scopes.
 *
 * Every helper answers for a Wireline & Slickline item, line or quote only and
 * returns null for anything else, so the shared screens keep exactly what they
 * rendered before for every other vertical.
 *
 * THE API DECIDES. Nothing here prices a line, picks a winning rate, or decides
 * which items may be quoted: an item's `refusal` is the API's (a rate tied with
 * another in the ticket's winning tier), and the screen only declines to offer it.
 */
import { wlApiError, WL_PERMISSION_REFUSAL } from './wireline-slickline';
import { wlJobRefusal } from './wireline-slickline-jobs';

export const WL_QUOTE_VERTICAL = 'wireline_slickline';
export const WL_QUOTE_SOURCE_KIND = 'wl_rate';

/**
 * The terms beside a rate-card item, never priced: its code, kind, what it
 * applies to (a crew position, a unit or type) and the card's notes. Null for
 * an item from any other source.
 */
export function wlQuoteItemTerms(item: { sourceKind?: string | null; terms?: Record<string, unknown> | null }): string | null {
  if (item.sourceKind !== WL_QUOTE_SOURCE_KIND) return null;
  const t = item.terms ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const parts = [str(t.code), str(t.kindLabel), str(t.depthBasisLabel), str(t.shotBasisLabel), str(t.appliesTo), str(t.notes)].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The currency a Wireline & Slickline quote's money is spelled in: the quote row's
 * own code. Null for any other vertical, which keeps fmtCurrency's bare "$" there.
 */
export function wlQuoteCurrency(quote: { vertical?: string | null; currency?: string | null } | null | undefined): string | null {
  if (!quote || quote.vertical !== WL_QUOTE_VERTICAL) return null;
  return quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
}

/**
 * The Type column for a line priced from the Wireline & Slickline Rate Card.
 * The API writes every such line as line_item_type 'other', so the kind is read
 * from the unit it stamps (api wlQuoteUnit: per_run, per_shot, per_gun,
 * per_hour, per_day, each and third_party as they are, flat → flat_fee, depth →
 * per_metre / per_foot, distance → per_km / per_mile). Null for any other line;
 * a Wireline & Slickline line with an unknown unit is `—`, never "Other".
 */
const WL_QUOTE_UNIT_KIND: Record<string, string> = {
  per_run: 'Per run',
  per_metre: 'Per depth',
  per_foot: 'Per depth',
  per_shot: 'Per shot',
  per_gun: 'Per gun',
  per_hour: 'Per hour',
  per_day: 'Per day',
  each: 'Each',
  flat_fee: 'Flat charge',
  per_km: 'Per distance',
  per_mile: 'Per distance',
  third_party: 'Third-party cost',
};
export function wlQuoteLineKind(line: { source_kind?: string | null; unit?: string | null }): string | null {
  if (line.source_kind !== WL_QUOTE_SOURCE_KIND) return null;
  return (line.unit && WL_QUOTE_UNIT_KIND[line.unit]) || '—';
}

/**
 * Every refusal the two Wireline & Slickline quote doors answer with. `api: true`
 * means the API's own words win when it sent some; `api: false` codes answer
 * in words written for a developer, so this sentence is always used.
 */
const WL_QUOTE_REFUSALS: Record<string, { api: boolean; sentence: string }> = {
  // POST /quotes/from-pricing-source
  QUOTE_RATE_AMBIGUOUS: { api: true, sentence: 'More than one rate on the Wireline & Slickline Rate Card applies to this line, so it cannot be quoted. Fix the rate card, then quote it.' },
  QUOTE_ITEM_NOT_AVAILABLE: { api: true, sentence: 'One of the picked rates no longer applies to this customer today. Go back and pick again.' },
  QUOTE_CURRENCY_MISMATCH: { api: true, sentence: "One of the picked rates is in a different currency from the company's, so it cannot be quoted." },
  QUOTE_QUANTITY_INVALID: { api: true, sentence: 'Enter a quantity greater than zero on every picked line.' },
  QUOTE_LINE_UNPRICED: { api: true, sentence: 'One of the picked lines has no rate on the Wireline & Slickline Rate Card (a third-party rate is priced on the ticket), so it cannot be quoted.' },
  VERTICAL_NOT_ENABLED: { api: false, sentence: 'Wireline & Slickline is not turned on for this company, so it cannot be quoted.' },
  // POST /quotes/:id/create-wl-job
  QUOTE_NOT_FOUND: { api: false, sentence: 'This quote was not found. It may have been deleted.' },
  QUOTE_NOT_WIRELINE_SLICKLINE: { api: true, sentence: 'Only a Wireline & Slickline quote becomes a Wireline & Slickline job.' },
  QUOTE_NOT_ACCEPTED: { api: true, sentence: 'Accept the quote before creating its Wireline & Slickline job.' },
  QUOTE_CUSTOMER_REQUIRED: { api: true, sentence: 'The quote has no customer to create a Wireline & Slickline job for.' },
  WELL_REQUIRED: { api: false, sentence: "Pick the well this job is on. A Wireline & Slickline job always belongs to one of the customer's wells." },
  SCOPES_REQUIRED: { api: false, sentence: 'Pick what the job is: at least one run kind (slickline, logging, perforating, …).' },
  well_customer_mismatch: { api: false, sentence: "That well belongs to a different customer. Pick one of this quote's customer's wells." },
  not_found: { api: false, sentence: 'That well, unit or supervisor was not found. It may have been deleted. Pick it again.' },
  unit_invalid: { api: false, sentence: 'That unit could not be read. Pick the unit again.' },
  QUOTE_JOB_ALREADY_CREATED: { api: true, sentence: "This quote's Wireline & Slickline job was just created. Refresh to see it." },
  SCHEDULED_DATE_INVALID: { api: false, sentence: 'The job day is not a valid date. Pick it again or leave it blank.' },
};

export const WL_QUOTE_REFUSAL_CODES = Object.keys(WL_QUOTE_REFUSALS);

/**
 * A refused Wireline & Slickline quote write as `{ code, error }` — the shape the
 * quote screens' refusal notice draws. A 403 is a refusal of permission
 * whatever the body says (the create-job door is admin / office staff). A code
 * this map lacks — the WL job parser's own refusals (an invalid run kind, a
 * unit or supervisor not this tenant's) — reads through the WL job map, else the API's words.
 */
export function wlQuoteRefusal(err: unknown, fallback: string): { code?: string; error: string } {
  const e = wlApiError(err);
  const code = e.code ?? undefined;
  if (e.status === 403 && code !== 'VERTICAL_NOT_ENABLED') return { code, error: WL_PERMISSION_REFUSAL };
  const known = code ? WL_QUOTE_REFUSALS[code] : undefined;
  if (known) return { code, error: known.api && e.message ? e.message : known.sentence };
  return { code, error: wlJobRefusal(err, fallback) };
}
